/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 筛选提交逻辑 Hook
 */
import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// @ts-expect-error legacy type compatibility
import md5 from 'js-md5'
import { trackPicsPageClick } from '../utils/sensorUtils'
import { FilterState } from '../types'
import {
  trackFilterSubmit,
  trackFilterSuccess,
  generateRecommendedWordSource
} from '../utils/sensorUtils'
import { sensorsRole } from '../../../../utils/sensors'
import { selectOldPhotosCount } from '../../../../slices/photosByContentSlice'
import { setSearchTrackingId } from '../../../../slices/photoFilterSlice'
import { getInitialFilter } from '../utils/filterUtils'

// 搜索结果数据类型
export interface SearchResult {
  total?: number
  expandInfo?: {
    expandSegment?: Array<{ word: string }>
    expandPrecise?: {
      canPreciseSearch?: boolean
    }
  }
  suggestedSearchWords?: Array<{ word: string }>
  photosByLocation?: {
    photosNum?: number
  }
}

/**
 * 筛选提交逻辑 Hook
 */
export const useFilterSubmit = (params: {
  groupId: string
  myRole: number
  members: any
  allWatermarksNum: number
  onSubmit: (
    filter: FilterState
  ) => void | Promise<void> | Promise<SearchResult | void> | SearchResult
  onReset?: () => void | Promise<void>
  updateFilter: (updates: Partial<FilterState>) => void
  updateQueryFilter: (filter: FilterState) => void
  setLastPageCond: (cond: string) => void
  setIsAscSort: (isAsc: boolean) => void
  closeAllModals: () => void
  pageType?: string
  formPlace?: string
  getInputKeywords?: () => string[]
  getLabelName?: () => string
}) => {
  const dispatch = useDispatch()
  const {
    groupId,
    myRole,
    members,
    allWatermarksNum,
    onSubmit,
    onReset,
    updateQueryFilter,
    setLastPageCond,
    setIsAscSort,
    closeAllModals,
    pageType = 'allPhoto',
    formPlace,
    getInputKeywords,
    getLabelName
  } = params

  const oldPhotosCount = useSelector(selectOldPhotosCount)
  const userID = useSelector((state: any) => state.main?.user?.user_id || '')

  const filterKeywordsRef = useRef<string>('')
  const filterDateRef = useRef<Date | null>(null)

  // 构建筛选成功埋点参数
  const buildTrackFilterSuccessParams = useCallback(
    (
      filter: FilterState,
      preparedFilter: FilterState,
      searchResult: SearchResult | null,
      processTime: number
    ) => {
      // 计算 tabList
      const tabList: number[] = [1] // 1 表示内容标签，2表示地点标签（已去除）
      if (searchResult?.expandInfo?.expandPrecise?.canPreciseSearch) {
        tabList.push(3) // 3 表示精准搜索标签
      }

      // 生成 searchTrackingId
      const keywordStr = preparedFilter.searchKeywords.join('|')
      if (filterKeywordsRef.current !== keywordStr) {
        filterKeywordsRef.current = keywordStr
        filterDateRef.current = new Date()
      }

      const searchTrackingId = (md5 as any)(
        keywordStr +
          userID +
          (filterDateRef.current?.getTime() || new Date().getTime())
      )
      dispatch(setSearchTrackingId(searchTrackingId))

      // 获取搜索结果数据
      const firstPageNum = searchResult?.total || 0
      const recommendParticipleList =
        searchResult?.expandInfo?.expandSegment?.map((item) => item.word) || []
      const suggestedWords =
        searchResult?.suggestedSearchWords?.map((item) => item.word) || []

      // 生成推荐词来源
      const recommendedWordSource = generateRecommendedWordSource(
        preparedFilter.searchKeywords
      )

      // 格式化时间规则
      const timeRule = preparedFilter.times
        ? [
            preparedFilter.times
              .map((item) => item.format('YY年M月D日'))
              .join('-')
          ]
        : []

      // 判断搜索类型：AI搜索 或 普通搜索
      const searchResultType = preparedFilter.isNlpFilter
        ? 'AI搜索'
        : '普通搜索'
      // 用户实际输入的文本
      const searchText =
        preparedFilter.keyword ||
        filter.keyword ||
        filter.searchKeywords.join(' ')

      // 获取文件类型
      const fileType =
        preparedFilter.mediaType === null
          ? 'all'
          : preparedFilter.mediaType === 0
            ? 'photo'
            : 'video'

      // 构建埋点参数
      const trackParams: any = {
        processTime,
        firstPageNum,
        recommendParticipleList,
        keyword: keywordStr,
        recommendedWordSource,
        list1: tabList,
        fromPlace: formPlace,
        timeRule,
        number1: oldPhotosCount,
        list2: suggestedWords,
        string3: searchTrackingId,
        searchResultType,
        searchText,
        role: sensorsRole(myRole),
        groupID: groupId,
        fileType
      }

      // 如果是 AI 搜索，添加 string1 和 list3
      if (preparedFilter.isNlpFilter) {
        if (preparedFilter.nlpResponseJson) {
          trackParams.string1 = preparedFilter.nlpResponseJson
        }
        if (
          preparedFilter.departmentUsers &&
          preparedFilter.departmentUsers.length > 0
        ) {
          trackParams.list3 = preparedFilter.departmentUsers.map(
            (user) => user.name
          )
        }
        trackParams.list4 = preparedFilter.nlpDepartmentList || []
      } else {
        // 正常搜索也要添加list3和list4
        trackParams.list3 = preparedFilter.departmentUsers.map(
          (user: any) => user.name
        )
        trackParams.list4 = preparedFilter.departments.map(
          (dept: any) => dept.name
        )
      }
      trackParams.TextContent = preparedFilter.nlpTextContent || ''
      return trackParams
    },
    [
      dispatch,
      userID,
      oldPhotosCount,
      formPlace,
      myRole,
      groupId,
      getInputKeywords
    ]
  )

  // 准备提交的筛选条件（处理业务逻辑）
  const prepareFilterForSubmit = useCallback(
    (filter: FilterState): FilterState => {
      const newFilter: FilterState = {
        ...filter,
        useSegment: true
      }

      // 如果 searchKeywords 数组长度不等于 1，则 suggestedSearchWords 设置为空数组
      if (filter.searchKeywords.length !== 1) {
        newFilter.suggestedSearchWords = []
      }

      return newFilter
    },
    []
  )

  // 执行提交
  const executeSubmit = useCallback(
    async (filter: FilterState) => {
      const preparedFilter = prepareFilterForSubmit(filter)

      // 更新 queryFilter
      updateQueryFilter(preparedFilter)

      // 记录请求开始时间
      const requestStartTime = new Date().getTime()

      // 埋点：筛选开始
      trackFilterSubmit({
        filter: preparedFilter,
        groupId,
        myRole,
        members,
        allWatermarksNum,
        pageType,
        labelName: getLabelName?.()
      })

      // 调用父组件的 onSubmit，并获取返回结果
      const result = await onSubmit(preparedFilter)
      trackPicsPageClick({
        clickItem: 'beginSearch',
        keyword: generateRecommendedWordSource(preparedFilter.searchKeywords),
        number1: result?.suggestedSearchWords?.length || 0,
        number2:
          result?.suggestedSearchWords?.filter((item: any) => item.isChecked)
            .length || 0,
        list1: result?.suggestedSearchWords?.map((item) => item.word) || [],
        searchText: preparedFilter.searchKeywords.join(','),
        string3: (md5 as any)(
          filter.searchKeywords.join('|') +
            userID +
            (filterDateRef.current?.getTime() || new Date().getTime())
        )
      })

      // 处理验收关键词
      if (preparedFilter.searchKeywords.includes('验收')) {
        localStorage.setItem('acceptanceSearched', 'true')
      }

      // 如果 onSubmit 返回了结果数据，上报成功埋点
      if (result && typeof result === 'object' && 'total' in result) {
        const searchResult = result as SearchResult

        // 计算处理时间
        const processTime = new Date().getTime() - requestStartTime

        // 构建埋点参数
        const trackParams = buildTrackFilterSuccessParams(
          filter,
          preparedFilter,
          searchResult,
          processTime
        )

        // 上报成功埋点
        trackFilterSuccess({
          ...trackParams,
          fromType: '全部照片页beginSearch'
        })
      }
    },
    [
      prepareFilterForSubmit,
      updateQueryFilter,
      groupId,
      myRole,
      members,
      allWatermarksNum,
      pageType,
      onSubmit,
      buildTrackFilterSuccessParams
    ]
  )

  // 处理筛选提交（这个函数现在由主组件直接调用 executeSubmit）
  const handleSubmit = useCallback(
    (filter: FilterState) => {
      closeAllModals()
      setLastPageCond('')

      // 执行提交
      return executeSubmit(filter)
    },
    [closeAllModals, setLastPageCond, executeSubmit]
  )

  // 处理重置
  const handleReset = useCallback(async () => {
    setLastPageCond('')

    // 获取重置后的筛选条件（初始筛选条件）
    const resetFilter = getInitialFilter()
    const preparedResetFilter = prepareFilterForSubmit(resetFilter)

    // 更新 queryFilter
    updateQueryFilter(preparedResetFilter)

    // 记录请求开始时间
    const requestStartTime = new Date().getTime()

    // 调用 onReset 并获取结果（如果有）
    let resetResult: SearchResult | null = null
    if (onReset) {
      const result = await onReset()
      // 如果 onReset 返回了结果，尝试使用它
      if (
        result !== undefined &&
        result !== null &&
        typeof result === 'object' &&
        'total' in result
      ) {
        resetResult = result as SearchResult
      }
    }

    // 计算处理时间
    const processTime = new Date().getTime() - requestStartTime

    // 构建埋点参数
    const trackParams = buildTrackFilterSuccessParams(
      resetFilter,
      preparedResetFilter,
      resetResult,
      processTime
    )

    // 上报成功埋点
    trackFilterSuccess({ ...trackParams, fromType: '全部照片页beginSearch' })
  }, [
    setLastPageCond,
    dispatch,
    onReset,
    prepareFilterForSubmit,
    updateQueryFilter,
    buildTrackFilterSuccessParams
  ])

  return {
    handleSubmit,
    handleReset,
    prepareFilterForSubmit,
    executeSubmit
  }
}
